import format from 'date-fns/format';
import { config } from '@uhg-abyss/web/tools/config';
import pdfLogo from '@src/app/App/Content/Confirmation/print/pdf_logo.png';

// Add print styles to the document
const injectPrintStyles = () => {
  if (typeof window === 'undefined') return;

  const existingStyle = document.getElementById('tin-status-print-styles');
  if (existingStyle) return; // Already injected

  const styleElement = document.createElement('style');
  styleElement.id = 'tin-status-print-styles';
  styleElement.textContent = `
    @media print {
      body * {
        visibility: hidden;
      }

      #print-status,
      #print-status * {
        visibility: visible;
      }

      #print-status {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
      }

      #Status-downloadButton, [id*="downloadButton"],
      #learn-about-uhcprovider, #another-tin-status-search, #myaccount-button {
        display: none !important;
      }

      * {
        box-shadow: none !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .print-header {
        display: flex !important;
        justify-content: space-between;
        margin-bottom: 30px;
        padding-bottom: 15px;
      }

      .print-header-logo {
        max-width: 200px;
        height: auto;
      }

      .print-header-title {
        text-align: center;
        color: #002677;
        font-size: 20px;
        font-family: 'sans-serif';
        font-weight: bold;
      }

      .print-header-date {
        color: #002677;
        font-size: 18px;
        font-family: 'sans-serif';
        font-weight: bold;
        white-space: nowrap;
      }
    }

    @media screen {
      .print-header {
        display: none;
      }
    }
  `;
  document.head.appendChild(styleElement);
};

const createPrintHeader = async () => {
  const header = document.createElement('div');
  header.className = 'print-header';

  // Logo
  const logo = document.createElement('img');
  logo.className = 'print-header-logo';
  const imgSrc =
    typeof pdfLogo === 'string' ? pdfLogo : pdfLogo?.src || pdfLogo?.default;

  try {
    // Convert image to data URL for better print compatibility
    let imgPath;
    if (config('APP_ENV') === 'local') {
      imgPath = imgSrc;
    } else {
      imgPath = `${config('PUBLIC_IMAGE')}${imgSrc}`;
    }

    const response = await fetch(imgPath);
    const blob = await response.blob();
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(blob);
    });
    logo.src = dataUrl;
  } catch (error) {
    // Fallback to direct path if fetch fails
    console.warn('Failed to load logo as data URL, using direct path:', error);
    if (config('APP_ENV') === 'local') {
      logo.src = imgSrc;
    } else {
      logo.src = `${config('PUBLIC_IMAGE')}${imgSrc}`;
    }
  }

  logo.alt = 'UnitedHealthcare Logo';

  // Title
  const title = document.createElement('div');
  title.className = 'print-header-title';
  title.textContent = 'Tin Registration Status';

  // Date
  const date = document.createElement('div');
  date.className = 'print-header-date';
  date.textContent = format(new Date(), 'MM/dd/yyyy');

  header.appendChild(logo);
  header.appendChild(title);
  header.appendChild(date);

  return header;
};

export const print = {
  printPage: () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  },
  savePDF: async (element, fileName, callback) => {
    if (typeof window === 'undefined') {
      if (callback) callback();
      return;
    }

    // Inject print styles
    injectPrintStyles();

    // Add print header if it doesn't exist
    const statusElement = document.getElementById('print-status');
    if (!statusElement) {
      if (callback) callback();
      return;
    }

    let printHeader = statusElement.querySelector('.print-header');
    if (!printHeader) {
      printHeader = await createPrintHeader();
      statusElement.insertBefore(printHeader, statusElement.firstChild);
    }

    // Set document title for PDF filename suggestion
    const originalTitle = document.title;
    document.title = fileName;

    // Restore original title after print dialog closes
    const handleAfterPrint = () => {
      document.title = originalTitle;
      if (callback) callback();
    };
    window.addEventListener('afterprint', handleAfterPrint, { once: true });

    // Trigger print dialog
    window.print();
  },
};
